import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import ProcessFlow from "@/components/ProcessFlow";
import { Container, Reveal, SectionHead } from "@/components/Motion";
import { IMAGES, SERVICES } from "@/data/content";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);
  const Icon = service.icon;

  return (
    <>
      <SEO title={`${service.title} | EVOVLEX IT`} description={service.meta} />
      <PageHero
        eyebrow="Services"
        titleLines={[service.title]}
        sub={service.short}
        image={IMAGES.hero}
      />

      <section data-testid="service-detail-overview" className="bg-white py-20 lg:py-28">
        <Container>
          <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-evo-blue">
                  <Icon className="h-7 w-7" strokeWidth={1.7} />
                </span>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                  {service.headline}
                </h1>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">{service.intro}</p>
              </Reveal>
              {service.outro && (
                <Reveal delay={0.24}>
                  <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{service.outro}</p>
                </Reveal>
              )}
              {service.positioningStatement && (
                <Reveal delay={0.28}>
                  <p className="mt-8 border-l-2 border-cyan-500 pl-5 font-display text-xl font-semibold text-navy-900">
                    {service.positioningStatement}
                  </p>
                </Reveal>
              )}
              {service.positioning && (
                <Reveal delay={0.28}>
                  <div className="mt-8 flex flex-wrap gap-2.5">
                    {service.positioning.map((p) => (
                      <span
                        key={p}
                        data-testid={`positioning-chip-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </Reveal>
              )}
              <Reveal delay={0.34}>
                <Link
                  to="/contact"
                  data-testid="service-detail-cta"
                  className="mt-10 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:-translate-y-0.5"
                >
                  Talk to an Expert
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <SectionHead eyebrow="Capabilities" title={`Our ${service.title} Services`} />
              <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {service.capabilities.map((cap, i) => (
                  <Reveal key={cap} delay={0.04 * (i % 6)}>
                    <div
                      data-testid={`capability-${cap.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-5 py-4 transition-all duration-300 hover:border-cyan-500/40 hover:bg-white hover:shadow-md"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-evo-blue transition-colors group-hover:bg-evo-blue group-hover:text-white">
                        <Check className="h-4 w-4" strokeWidth={2.4} />
                      </span>
                      <span className="text-sm font-semibold text-slate-800">{cap}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>

          {service.philosophy && (
            <div className="mt-20">
              <Reveal>
                <div className="relative overflow-hidden rounded-2xl bg-navy-950 p-8 lg:p-12 grain">
                  <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
                  <div className="relative">
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-400">
                      {service.philosophy.label}
                    </p>
                    <div className="mt-8">
                      <ProcessFlow steps={service.philosophy.steps} dark testid="service-philosophy-process" />
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          )}
        </Container>
      </section>

      <section data-testid="service-detail-more" className="border-t border-slate-200 bg-slate-50 py-16 lg:py-20">
        <Container>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Explore More Services</h2>
            <Link
              to="/services"
              data-testid="service-detail-all-services"
              className="inline-flex items-center gap-2 text-sm font-semibold text-evo-blue hover:text-cyan-600"
            >
              View all services
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="group flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-500/40 hover:shadow-lg"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-evo-blue transition-colors group-hover:bg-evo-blue group-hover:text-white">
                  <s.icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <span className="text-sm font-semibold text-slate-900">{s.title}</span>
              </Link>
            ))}
          </div>
        </Container>
      </section>
    </>
  );
}
